import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { Spacer } from '@kizen/kds/Spacer';
import { Typography } from '@kizen/kds/Typography';
import { colorsButton } from 'app/colors';
import Icon from '__components/Kizen/Icon';
import IconButtonMenu from '__components/Kizen/IconButtonMenu';
import {
  type BusinessAppPermissions,
  formatDateTime,
  releaseHistory,
} from 'ts-components/Plugins/businessApps/helpers';
import type { PendingBusinessAppAction } from 'ts-components/Plugins/businessApps/useBusinessAppActions';
import {
  BUSINESS_APP_ACTIONS,
  EMPTY_VALUE,
} from 'ts-components/Plugins/businessApps/constants';
import type { BusinessAppDetails } from 'ts-components/Plugins/businessApps/types';
import { useActionLabels } from '../Apps/columns';

const StyledList = styled.div`
  display: flex;
  flex-direction: column;

  & > * + * {
    border-top: 1px solid var(--color-gray-400);
  }
`;

type VersionHistoryPanelProps = {
  details: BusinessAppDetails;
  permissions: BusinessAppPermissions;
  onSelectAction: (request: PendingBusinessAppAction) => void;
};

export const VersionHistoryPanel: FC<VersionHistoryPanelProps> = ({
  details,
  permissions,
  onSelectAction,
}) => {
  const { t } = useTranslation();
  const actionLabels = useActionLabels();
  const released = releaseHistory(details.versions);
  const versionOptions = [
    {
      value: BUSINESS_APP_ACTIONS.deleteVersion,
      label: actionLabels[BUSINESS_APP_ACTIONS.deleteVersion],
    },
  ];

  return (
    <div>
      <Typography size="2xl" weight="semibold" variant="header">
        {t('Version History')}
      </Typography>
      <Spacer size={20} />
      <StyledList>
        {released.map((version) => {
          const date = formatDateTime(
            t,
            version.published_at ?? version.updated
          );
          const name = version.published_by?.full_name ?? EMPTY_VALUE;
          return (
            <div
              className="flex items-center gap-[20px] py-[12px]"
              key={version.version}
            >
              <div className="w-[80px]">
                <Typography weight="semibold">{version.version}</Typography>
              </div>
              <div className="w-[130px]">
                {version.status === 'live' ? (
                  <Typography color="feedback-success">
                    {t('Current Version')}
                  </Typography>
                ) : null}
              </div>
              <div className="flex-1">
                <Typography>
                  {version.built_by &&
                  version.built_by.id !== version.published_by?.id
                    ? t(
                        'Published {{date}} by {{name}}, built by {{builder}}',
                        {
                          date,
                          name,
                          builder: version.built_by.full_name ?? EMPTY_VALUE,
                        }
                      )
                    : t('Published {{date}} by {{name}}', { date, name })}
                </Typography>
              </div>
              {permissions.canPublishBusinessApps ? (
                <IconButtonMenu
                  sizing="dense"
                  position="right"
                  color={colorsButton.iconGray}
                  title={t('Select Action')}
                  options={versionOptions}
                  onChange={() =>
                    onSelectAction({
                      action: BUSINESS_APP_ACTIONS.deleteVersion,
                      app: details,
                      version,
                    })
                  }
                  data-qa-id={`business-app-version-actions-${version.version}`}
                >
                  <Icon icon="three-dot" />
                </IconButtonMenu>
              ) : null}
            </div>
          );
        })}
      </StyledList>
    </div>
  );
};
